/** Máximo de dígitos nacionais (DDD + número) para telefone BR. */
export const BRAZIL_PHONE_MAX_NATIONAL_DIGITS = 11;

/**
 * Comprimento máximo da string mascarada com 11 dígitos: `(XX) 9XXXX-XXXX` (15 caracteres).
 * Usar em `maxlength` do input como rede de segurança.
 */
export const BRAZIL_PHONE_MASK_MAX_LENGTH = 15;

/**
 * Máscara progressiva BR: (XX) XXXXX-XXXX ou (XX) XXXX-XXXX.
 * Sempre limita a {@link BRAZIL_PHONE_MAX_NATIONAL_DIGITS} dígitos (descarta o excesso).
 */
export function maskBrazilPhoneInput(raw: string): string {
  const digitsOnly = raw.replace(/\D/g, '');
  const digits = digitsOnly.slice(0, BRAZIL_PHONE_MAX_NATIONAL_DIGITS);
  if (!digits) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

/**
 * Valor enviado na API: E.164 para BR com 10 ou 11 dígitos nacionais, ou já com código 55 (12–13 dígitos).
 * APIs .NET / PhoneAttribute costumam esperar este formato; só dígitos locais costumam falhar validação.
 */
export function normalizeBrazilPhoneForSubmit(maskedOrRaw: string): string {
  const trimmed = (maskedOrRaw || '').trim();
  if (!trimmed) return '';
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10 || digits.length === 11) {
    return `+55${digits}`;
  }
  if (digits.startsWith('55') && digits.length >= 12 && digits.length <= 13) {
    return `+${digits}`;
  }
  return trimmed;
}

/**
 * Exibe valor armazenado (dígitos, E.164 +55… ou mascarado) no padrão (XX) XXXXX-XXXX.
 */
export function formatStoredBrazilPhoneForDisplay(stored: string): string {
  const digits = stored.replace(/\D/g, '');
  let national = digits;
  if (national.startsWith('55') && national.length > 11) {
    national = national.slice(2);
  }
  national = national.slice(0, BRAZIL_PHONE_MAX_NATIONAL_DIGITS);
  if (!national.length) return '';
  return maskBrazilPhoneInput(national);
}

/** `true` se vazio (limpar) ou se dá para enviar E.164 válido à API; `false` se ainda incompleto / inválido. */
export function isBrazilPhoneE164Persistable(maskedOrRaw: string | null | undefined): boolean {
  const t = (maskedOrRaw ?? '').trim();
  if (!t.length) return true;
  const n = normalizeBrazilPhoneForSubmit(t);
  return /^\+[1-9]\d{6,14}$/.test(n);
}
